import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ChevronDown, CalendarCheck, Download } from 'lucide-react';
import { FloatingShapes } from './MemphisShapes';

// Masked line-by-line reveal for the kinetic headline
const MaskedLine = ({ children, delay = 0, className = "" }) => (
  <span className="block overflow-hidden py-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ delay, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end start"]
  });
  const logoY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const contentY = useTransform(scrollYProgress, [0, 1], [0, 220]);
  const shapesY = useTransform(scrollYProgress, [0, 1], [0, -160]);

  const scrollToMenu = () => {
    if (window.__lenis) {
      window.__lenis.scrollTo('#menu');
    } else {
      document.getElementById('menu')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleBooking = () => {
    window.open('https://santomato.plateform.app/r/prenotazionerapidasantomato', '_blank');
  };

  return (
    <section
      id="hero"
      ref={sectionRef}
      data-testid="hero-section"
      className="min-h-screen bg-santomato-yellow relative flex flex-col items-center justify-center overflow-hidden"
    >
      {/* Parallax Memphis shapes */}
      <motion.div style={{ y: shapesY }} className="absolute inset-0">
        <FloatingShapes />
      </motion.div>

      {/* Top right actions - Fixed */}
      <div className="fixed top-4 right-4 md:top-6 md:right-6 z-50 flex flex-col items-end gap-3">
        <motion.button
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.6, type: "spring", bounce: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleBooking}
          data-testid="hero-booking-button"
          className="btn-brutal bg-santomato-red text-white font-fredoka text-base md:text-lg px-6 md:px-8 py-4 rounded-full flex items-center gap-2"
        >
          <CalendarCheck size={28} strokeWidth={2.5} />
          <span>PRENOTA</span>
        </motion.button>
        <motion.a
          href="/menu-asporto-santomato.pdf"
          download="Menu-Asporto-Santomato.pdf"
          initial={{ opacity: 0, x: 100 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 1.8, type: "spring", bounce: 0.5 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="hero-download-button"
          className="btn-brutal bg-santomato-yellow text-black font-fredoka text-xs md:text-sm px-4 md:px-5 py-3 rounded-full flex items-center gap-2"
        >
          <Download size={20} strokeWidth={2.5} />
          <span>SCARICA IL MENÙ ASPORTO</span>
        </motion.a>
      </div>

      {/* Main Content */}
      <motion.div style={{ y: contentY }} className="relative z-10 text-center px-4 md:px-8 max-w-6xl mx-auto">
        {/* Logo with parallax + float */}
        <motion.div style={{ y: logoY }} className="mb-2 md:mb-4">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ type: "spring", bounce: 0.5, duration: 1 }}
          >
            <motion.img
              src="https://customer-assets.emergentagent.com/job_santomato-pizza/artifacts/905354re_Tondo%20trasp.png"
              alt="SANTOMATO - Pizza Lovers Since 1985"
              data-testid="hero-logo"
              className="w-52 h-52 md:w-64 md:h-64 lg:w-72 lg:h-72 mx-auto drop-shadow-2xl object-contain"
              animate={{ y: [0, -15, 0] }}
              transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
            />
          </motion.div>
        </motion.div>

        {/* Kinetic masked headline */}
        <h1
          className="font-fredoka font-bold uppercase leading-[0.95] mb-5 md:mb-6 text-5xl sm:text-6xl md:text-7xl lg:text-7xl"
          data-testid="hero-headline"
        >
          <MaskedLine delay={0.5} className="text-black">
            GRANDE.
          </MaskedLine>
          <MaskedLine delay={0.65} className="text-santomato-red">
            SOTTILE.
          </MaskedLine>
          <MaskedLine delay={0.8} className="text-outline">
            CROCCANTE.
          </MaskedLine>
        </h1>

        {/* Subheadline */}
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.6 }}
          className="font-nunito font-bold text-base sm:text-lg md:text-xl text-black mb-7 md:mb-8 max-w-2xl mx-auto"
          data-testid="hero-subheadline"
        >
          Pizza grande e generosa, sottile e croccante.{" "}
          <span className="text-santomato-red">Senza fronzoli.</span>
        </motion.p>

        {/* CTA Button */}
        <motion.button
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.3, duration: 0.4 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={scrollToMenu}
          data-testid="hero-cta-button"
          className="btn-brutal bg-santomato-red text-white font-fredoka text-lg md:text-xl px-8 md:px-12 py-4 md:py-5 rounded-full"
        >
          SCOPRI IL MENU
        </motion.button>
      </motion.div>

      {/* Mascot sticker */}
      <motion.div
        initial={{ opacity: 0, scale: 0, rotate: -20 }}
        animate={{ opacity: 1, scale: 1, rotate: -6 }}
        transition={{ delay: 1.5, type: "spring", bounce: 0.5 }}
        className="absolute bottom-12 right-4 md:right-10 z-10 hidden sm:block"
      >
        <motion.img
          src="https://customer-assets-lxgj4vgw.emergentagent.net/job_santomato-pizza/artifacts/583a93a616e8171e_image.png"
          alt="Mascotte Santomato in azione"
          data-testid="hero-mascot"
          loading="lazy"
          className="w-28 md:w-40 lg:w-48 drop-shadow-xl"
          animate={{ y: [0, -12, 0], rotate: [0, 3, -3, 0] }}
          transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
        />
      </motion.div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8 }}
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="cursor-pointer"
          onClick={scrollToMenu}
          data-testid="scroll-indicator"
        >
          <ChevronDown size={48} className="text-black" strokeWidth={3} />
        </motion.div>
      </motion.div>

      {/* Bottom decorative band */}
      <div className="absolute bottom-0 left-0 right-0 h-4 bg-black" />
    </section>
  );
};

export default Hero;
